import { FC } from 'react';
import { Empty } from 'antd';
import { Container } from '../../components/container/container';
import { ListingCard } from '../../components/cards/listing-card/listing-card';
import { Catalog } from '../../apis/search-catalog';
import striptags from 'striptags';
import { Button } from '../button/button';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
} from '@mui/material';

import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../routes';

import { SearchFilterBtn } from '../secretary/style';
import {
  CategoryFilter,
  CategoryFilter as DepartmentFilter,
} from './list.container';
import { Pagination } from '../../components';
import { PageBanner } from '../../components/page-banner/page-banner';
import { SearchBar } from '../../components/search-bar';

import styles from './list.module.css';
import { IDepartment } from '../../apis/department';
import { DriveFileRenameOutline, FilterAltOutlined } from '@mui/icons-material';

export interface ListProps {
  list?: Catalog[];
  onClick: (catalog: Catalog) => void;
  onClickFavourite: (slug: string) => void;
  onClickOnlineService: (link: string) => void;
  page: number;
  handleChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  toggleDrawer: (newOpen: boolean) => () => void;
  selectedCategories: CategoryFilter[];
  appliedCategories: CategoryFilter[];
  appliedDepartments: DepartmentFilter[];
  categories: { id: string; name: string; slug: string }[];
  departments: IDepartment[];
  selectedDepartments: DepartmentFilter[];
  handleCheckboxChange: (filter: CategoryFilter) => void;
  handleDepartmentCheckboxChange: (filter: DepartmentFilter) => void;
  handleFilterByCategory: () => void;
  open: boolean;
  resultTotal?: number;
  totalPages: number;
  disposition: 'line' | 'card';
  removeCategoryFilter: (slug: string) => void;
  removeDepartmentFilter: (slug: string) => void;
  onClickSearch: () => void;
  onChangeText: (text: string) => void;
  value: string;
  activeTab: 'all' | 'online';
  onTabChange: (tab: 'all' | 'online') => void;
  clearFilters: () => void;
  clearCategoriesFilter: () => void;
  clearDepartmentsFilter: () => void;
}

export const List: FC<ListProps> = ({
  list,
  onClick,
  onClickFavourite,
  onClickOnlineService,
  page,
  handleChange,
  toggleDrawer,
  selectedCategories,
  appliedCategories,
  categories,
  departments,
  selectedDepartments,
  appliedDepartments,
  handleCheckboxChange,
  handleDepartmentCheckboxChange,
  handleFilterByCategory,
  open,
  disposition,
  resultTotal,
  totalPages,
  removeCategoryFilter,
  removeDepartmentFilter,
  onClickSearch,
  onChangeText,
  value,
  activeTab,
  onTabChange,
  clearFilters,
  clearCategoriesFilter,
  clearDepartmentsFilter,
}) => {
  const history = useHistory();

  const handleRemoveFilter = (filter: {
    id: string;
    type: 'cat' | 'dep';
    name: string;
    slug: string;
  }) => {
    if (filter.type === 'cat') {
      removeCategoryFilter(filter.slug);
    } else {
      removeDepartmentFilter(filter.slug);
    }
  };

  const FilterDialog = (
    <Dialog
      open={open}
      onClose={toggleDrawer(false)}
      maxWidth="md"
      fullWidth
      aria-labelledby="filter-dialog-title"
      className={styles.filterDialog}
    >
      <DialogTitle className={styles.filterHeader}>
        <Box component="span" className={styles.headerTitle}>
          <FilterAltOutlined className={styles.filterIcon} />
          Filtrar por
        </Box>
        <IconButton
          aria-label="fechar"
          onClick={toggleDrawer(false)}
          className={styles.closeBtn}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={styles.dialogContent}>
        <Box component="section" className={styles.section}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <h3 className={styles.sectionTitle}>Categoria</h3>
            <button
              className={styles.filterButtonClearAll}
              onClick={clearCategoriesFilter}
              aria-label="Limpar todas as categorias"
            >
              Limpar categorias
            </button>
          </Box>

          <div className={styles.categoryGrid}>
            {categories?.map(({ id, name, slug }) => (
              <FormControlLabel
                key={id}
                className={styles.listFilterDialogLabel}
                style={{ fontSize: '12px !important' }}
                control={
                  <Checkbox
                    checked={selectedCategories.some((c) => c.id === id)}
                    onChange={() => handleCheckboxChange({ id, name, slug })}
                    size="small"
                  />
                }
                label={name}
              />
            ))}
          </div>
        </Box>

        <Box component="section" className={styles.section}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <h3 className={styles.sectionTitle}>Órgãos e Secretarias</h3>
            <button
              className={styles.filterButtonClearAll}
              onClick={clearDepartmentsFilter}
              aria-label="Limpar todos os órgãos"
            >
              Limpar órgãos
            </button>
          </Box>
          <div className={styles.categoryGrid}>
            {departments?.map(({ id, name, slug }) => (
              <FormControlLabel
                key={id}
                className={styles.listFilterDialogLabel}
                control={
                  <Checkbox
                    checked={selectedDepartments.some((d) => d.id === id)}
                    onChange={() =>
                      handleDepartmentCheckboxChange({ id, name, slug })
                    }
                    size="small"
                  />
                }
                label={departments.find((d) => d.id === id)?.shortName ?? name}
              />
            ))}
          </div>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleFilterByCategory}
          className={styles.filterButton}
          text="Preencher"
          iconComponent={
            <DriveFileRenameOutline className={styles.filterButtonIcon} />
          }
        ></Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {FilterDialog}

      <div className={styles.listContainer}>
        <PageBanner
          title={`Serviços Disponíveis (${resultTotal || 0})`}
          showBreadcrumb
        />

        <Container>
          <div className={styles.searchContainer}>
            <SearchBar
              onClickSearch={onClickSearch}
              onChangeText={onChangeText}
              value={value}
              placeholder="Buscar..."
            />
          </div>

          <div className={styles.headerContainer}>
            <div className={styles.tabGroup}>
              <button
                className={`${styles.tab} ${
                  activeTab === 'all' ? styles.active : ''
                }`}
                onClick={() => onTabChange('all')}
              >
                Todos
              </button>
              <button
                className={`${styles.tab} ${
                  activeTab === 'online' ? styles.active : ''
                }`}
                onClick={() => onTabChange('online')}
              >
                Online
              </button>
            </div>

            <SearchFilterBtn className={styles.filterButtonWrapper}>
              <Button
                onClick={toggleDrawer(true)}
                className={styles.headerButton}
                text="Filtrar"
                iconComponent={<FilterAltOutlined />}
              >
                Filtrar
              </Button>
              <button
                className={styles.filterButtonClearAll}
                onClick={clearFilters}
                aria-label="Limpar todos os filtros"
              >
                Limpar filtros
              </button>
            </SearchFilterBtn>
          </div>

          {appliedCategories.length + appliedDepartments.length > 0 &&
            (() => {
              const chips: {
                id: string;
                type: 'cat' | 'dep';
                name: string;
                slug: string;
              }[] = [
                ...appliedCategories.map((c) => ({
                  ...c,
                  type: 'cat' as const,
                  slug: c.slug || '',
                })),
                ...appliedDepartments.map((d) => ({
                  ...d,
                  type: 'dep' as const,
                  slug: d.slug || '',
                })),
              ];
              return (
                <div className={styles.selectedFilters}>
                  {chips.map((filter) => (
                    <button
                      key={`${filter.type}-${filter.id}`}
                      className={styles.filterChip}
                      onClick={() => handleRemoveFilter(filter)}
                      title="Remover filtro"
                    >
                      <CloseIcon
                        className={styles.chipClose}
                        fontSize="inherit"
                      />
                      <span className={styles.chipLabel}>
                        {filter.type === 'cat'
                          ? filter.name
                          : departments.find((d) => d.id === filter.id)
                              ?.shortName ?? filter.name}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })()}

          <div className={styles.gridWrapper}>
            <div className={styles.grid}>
              {list?.map((catalog) => {
                const href = history.createHref({
                  pathname: `${ROUTES.SERVICE_LIST_PAGE}/${catalog.slug}`,
                });

                return (
                  <div className={styles.card} key={catalog.title}>
                    <ListingCard
                      title={catalog.title}
                      category={catalog.category}
                      subtitle={striptags(catalog.description)}
                      type={catalog.isOnline ? 'online' : 'in-person'}
                      onClick={() => onClick(catalog)}
                      onClickFavourite={() => onClickFavourite(catalog.slug)}
                      onClickOnlineService={() =>
                        onClickOnlineService(catalog.link)
                      }
                      disposition={disposition}
                      isDigital={catalog.isDigital}
                      department={catalog.department}
                      href={href}
                    />
                  </div>
                );
              })}
            </div>

            {!list?.length && <Empty description="Nenhum serviço encontrado" />}

            {list && list.length > 0 ? (
              <Pagination
                count={totalPages}
                onChange={handleChange}
                page={page}
              />
            ) : (
              <div style={{ height: '5em' }} />
            )}
          </div>
        </Container>
      </div>
    </>
  );
};
